<template>
  <div class="logs-component" style="padding-top: 60px;">
    <!-- Header -->
    <div class="logs-header">
      <v-icon size="24" color="#0d2c6d" class="mr-2">mdi-text-box-search</v-icon>
      <span class="text-h6 font-weight-bold">לוגים</span>
    </div>

    <v-container fluid class="pa-0">
      <v-card class="logs-card">
        <!-- Filters Section -->
        <div class="filters-section">
          <div class="d-flex flex-wrap align-center">
            <v-text-field
              v-model="dateLogs"
              type="date"
              outlined
              dense
              hide-details
              class="filter-field mr-3"
            />

            <v-select
              v-model="filterLogs"
              :items="logsType"
              outlined
              dense
              hide-details
              label="סינון לוגים (בעמוד)"
              class="filter-field mr-3"
            />

            <v-select
              v-model="sortOrder"
              :items="sortOptions"
              outlined
              dense
              hide-details
              label="סדר תצוגה"
              class="filter-field mr-3"
            />

            <!-- Search: typed value only, applied on click -->
            <v-text-field
              v-model="searchInput"
              label="חיפוש בכל הלוגים"
              outlined
              dense
              hide-details
              class="filter-field mr-3 ml-3"
              prepend-inner-icon="mdi-magnify"
              @keyup.enter="applySearch"
            />

            <v-btn
              small
              outlined
              class="mr-2"
              :loading="progressShow"
              @click="applySearch"
            >
              <v-icon left small>mdi-magnify</v-icon>
              חפש
            </v-btn>

            <v-btn
              small
              text
              :disabled="!appliedSearch || progressShow"
              @click="clearSearch"
              class="mr-2"
            >
              נקה חיפוש
            </v-btn>

            <v-btn icon class="refresh-button" @click="refreshDetails" :loading="progressShow">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>

            <v-btn outlined small class="clean-cache-button mr-3" @click="cleanCache">
              <v-icon left small>mdi-cached</v-icon>
              ניקוי זיכרון זמני
            </v-btn>

            <div class="results-count mr-3">
              <span class="text-subtitle-2">תוצאות בעמוד: </span>
              <span class="font-weight-medium">{{ filteredLogs.length }}</span>
              <span v-if="appliedSearch" class="ml-2 text-caption">(חיפוש פעיל)</span>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <v-row v-if="progressShow" class="my-4">
          <v-col class="d-flex justify-center">
            <v-progress-circular indeterminate color="#0d2c6d" size="32" />
          </v-col>
        </v-row>

        <!-- Logs List -->
        <div class="logs-list" v-else>
          <div v-for="(log, index) in filteredLogs" :key="index" class="log-item">
            <!-- Log Header -->
            <div class="log-header">
              <div class="log-index">#{{ baseIndex + index + 1 }}</div>
              <div class="log-timestamp">{{ log.date }} {{ log.time }}</div>
              <v-chip small :color="getLevelColor(log.level)" text-color="white" class="log-level">
                {{ formatLevel(log.level) }}
              </v-chip>
            </div>

            <!-- Log Message -->
            <div class="log-message" v-html="formatLogMessage(log.message)" />
          </div>

          <!-- Empty State -->
          <div v-if="filteredLogs.length === 0" class="empty-state">
            <v-icon size="48" color="rgba(13, 44, 109, 0.1)" class="mb-4">mdi-text-box-search-outline</v-icon>
            <p class="text-subtitle-1 text-medium-emphasis">לא נמצאו לוגים</p>
          </div>
        </div>

        <!-- Server Pagination (cursor-based) -->
        <div class="pagination-section">
          <div class="pagination-info">
            <span class="text-body-2 text-medium-emphasis">
              עמוד {{ currentPage }}
              <span v-if="hasMore"> (יש עוד עמודים)</span>
              <span v-if="appliedSearch"> • חיפוש: "{{ appliedSearch }}"</span>
            </span>
          </div>

          <div class="d-flex align-center justify-center" style="gap: 10px;">
            <v-btn
              small
              outlined
              :disabled="!canGoPrev"
              @click="prevPage"
            >
              הקודם
            </v-btn>

            <v-btn
              small
              outlined
              :disabled="!canGoNext"
              @click="nextPage"
            >
              הבא
            </v-btn>
          </div>

          <div class="items-per-page">
            <v-select
              v-model="itemsPerPage"
              :items="itemsPerPageOptions"
              label="פריטים בעמוד"
              dense
              outlined
              hide-details
              class="items-per-page-select"
            />
          </div>
        </div>

        <!-- Debug info (הסר בפרודקשן) -->
        <div v-if="debugMode" class="debug-info pa-2" style="background: #f0f0f0; font-size: 12px; direction: ltr;">
          <pre>currentPage: {{ currentPage }}</pre>
          <pre>hasMoreByPage: {{ JSON.stringify(hasMoreByPage) }}</pre>
          <pre>cursorsByPage: {{ JSON.stringify(cursorsByPage) }}</pre>
          <pre>nextCursorByPage: {{ JSON.stringify(nextCursorByPage) }}</pre>
          <pre>canGoNext: {{ canGoNext }}</pre>
          <pre>canGoPrev: {{ canGoPrev }}</pre>
        </div>
      </v-card>
    </v-container>

    <SnackBar v-model="snackbar" :snacbarText="snacbarText" :snackbarColorBt="snackbarColorBt" />
  </div>
</template>

<script>
import SnackBar from '@/components/widgets/snackBar.vue'
import ApiServices from '@/services/api.service'
import Auth from '@/services/auth.service'

export default {
  name: 'LogsComp',
  components: {
    SnackBar,
  },
  data: () => ({
    overlay: false,
    editPlanDialog: false,
    snackbar: false,
    snackbarColorBt: 'success',
    snacbarText: '',
    progressShow: true,

    // logs של העמוד הנוכחי (אחרי parsing)
    logs: [],

    dateLogs: '',
    filterLogs: 'הכל',
    logsType: ['הכל', 'שגיאות', 'מידע'],

    // Search: input vs applied (applied only on button click)
    searchInput: '',
    appliedSearch: '',

    // paging מול השרת
    currentPage: 1,
    itemsPerPage: 100,
    itemsPerPageOptions: [
      { text: '100 פריטים', value: 100 },
      { text: '500 פריטים', value: 500 },
      { text: '1000 פריטים', value: 1000 },
    ],

    // cursor-based paging
    cursorsByPage: { 1: null }, // page -> cursor used to fetch that page
    nextCursorByPage: {},       // page -> nextCursor returned by server
    hasMoreByPage: {},          // page -> hasMore

    // מיון
    sortOrder: 'desc', // 'desc' = חדש לישן (ברירת מחדל), 'asc' = ישן לחדש
    sortOptions: [
      { text: 'חדש לישן', value: 'desc' },
      { text: 'ישן לחדש', value: 'asc' },
    ],

    // Debug mode - שנה ל-false בפרודקשן
    debugMode: false,

    token: localStorage.getItem('token'),
  }),

  computed: {
    // NOTE: filterLogs הוא סינון בעמוד הנוכחי בלבד (לא חיפוש גלובלי)
    filteredLogs() {
      let filtered = this.logs

      if (this.filterLogs === 'שגיאות') {
        filtered = filtered.filter((log) => log.level === 'error:' || log.level === 'exception:')
      } else if (this.filterLogs === 'מידע') {
        filtered = filtered.filter((log) => log.level !== 'error:' && log.level !== 'exception:')
      }

      return filtered
    },

    // תיקון: בדיקה ישירה של hasMoreByPage לעמוד הנוכחי
    hasMore() {
      // בדיקה מפורשת - אם הערך קיים והוא true
      const pageHasMore = this.hasMoreByPage[this.currentPage]
      return pageHasMore === true
    },

    // תיקון: canGoNext - האם אפשר ללכת לעמוד הבא
    canGoNext() {
      // לא בזמן טעינה
      if (this.progressShow) return false
      // יש עוד נתונים בשרת
      return this.hasMore
    },

    // תיקון: canGoPrev - האם אפשר ללכת לעמוד הקודם
    canGoPrev() {
      // לא בזמן טעינה
      if (this.progressShow) return false
      // לא בעמוד הראשון
      return this.currentPage > 1
    },

    baseIndex() {
      return (this.currentPage - 1) * this.itemsPerPage
    },
  },

  methods: {
    async getLogsPage(page = 1) {
      try {
        this.progressShow = true

        // cursor לעמוד המבוקש
        const cursor = Object.prototype.hasOwnProperty.call(this.cursorsByPage, page)
          ? this.cursorsByPage[page]
          : null

        let api =
          process.env.VUE_APP_BASE_URL +
          `/logs/get_today_logs?date=${this.dateLogs}` +
          `&workspaceId=${Auth.getWorkspaceId() || ''}` +
          `&limit=${this.itemsPerPage}` +
          `&sort=${this.sortOrder}`

        // global search (server-side)
        if (this.appliedSearch && this.appliedSearch.trim()) {
          api += `&q=${encodeURIComponent(this.appliedSearch.trim())}`
        }

        if (cursor !== null && cursor !== undefined && cursor !== '') {
          api += `&cursor=${encodeURIComponent(cursor)}`
        }

        console.log('[getLogsPage] Fetching page:', page, 'cursor:', cursor, 'api:', api)

        const res = await fetch(api, ApiServices.requestOptions('GET', '', this.token))
        const jsonObject = await res.json()

        console.log('[getLogsPage] Response:', {
          result: jsonObject?.result,
          linesCount: jsonObject?.lines?.length,
          nextCursor: jsonObject?.nextCursor,
          hasMore: jsonObject?.hasMore
        })

        if (res.status >= 400 || !jsonObject?.result) {
          this.showSnackBar(jsonObject?.message || 'שגיאה בקבלת נתונים', 'error')
          return
        }

        // השרת מחזיר lines מערך של שורות
        this.logs = (jsonObject.lines || []).map((line) => {
          const parts = line.split(' ')
          return {
            date: parts[0] || '',
            time: parts[1] || '',
            level: parts[2] || '',
            message: parts.slice(3).join(' '),
          }
        })

        // שמירת cursors כדי לאפשר next/prev
        // חשוב: לשמור את הערכים לפני עדכון currentPage
        this.$set(this.nextCursorByPage, page, jsonObject.nextCursor ?? null)
        this.$set(this.hasMoreByPage, page, jsonObject.hasMore === true)

        console.log('[getLogsPage] Updated hasMoreByPage:', this.hasMoreByPage)

        // הכנה לעמוד הבא: cursor שלו הוא nextCursor של העמוד הנוכחי
        const nextPage = page + 1
        if (jsonObject.hasMore && jsonObject.nextCursor !== null && jsonObject.nextCursor !== undefined) {
          this.$set(this.cursorsByPage, nextPage, jsonObject.nextCursor)
        }

        // עדכון העמוד הנוכחי בסוף
        this.currentPage = page
      } catch (error) {
        console.error('[getLogsPage] Error:', error)
        this.showSnackBar('שגיאה בקבלת לוגים: ' + error, 'error')
      } finally {
        this.progressShow = false
      }
    },

    nextPage() {
      console.log('[nextPage] Called. canGoNext:', this.canGoNext, 'hasMore:', this.hasMore)
      if (!this.canGoNext) return
      this.getLogsPage(this.currentPage + 1)
    },

    prevPage() {
      console.log('[prevPage] Called. canGoPrev:', this.canGoPrev)
      if (!this.canGoPrev) return
      this.getLogsPage(this.currentPage - 1)
    },

    resetPaging() {
      this.currentPage = 1
      this.cursorsByPage = { 1: null }
      this.nextCursorByPage = {}
      this.hasMoreByPage = {}
      this.logs = []
    },

    refreshDetails() {
      // רענון תמיד מהעמוד הראשון (לוגים הכי חדשים)
      this.resetPaging()
      this.getLogsPage(1)
    },

    applySearch() {
      // apply only on click/enter
      this.appliedSearch = (this.searchInput || '').trim()
      this.refreshDetails()
    },

    clearSearch() {
      this.searchInput = ''
      this.appliedSearch = ''
      this.refreshDetails()
    },

    formatLogMessage(message) {
      const keywords = /\b(SessionId|DataName|TableName|By|Message|Item|Error)\b/g
      return (message || '').replace(keywords, '<strong>$1</strong>')
    },

    getLevelColor(level) {
      switch (level) {
        case 'info:':
          return '#10B981'
        case 'error:':
          return '#EF4444'
        case 'exception:':
          return '#EF4444'
        default:
          return '#F59E0B'
      }
    },

    formatLevel(level) {
      if (level) return level.replace(':', '').toUpperCase()
      return ''
    },

    async cleanCache() {
      try {
        const api = process.env.VUE_APP_BASE_URL + '/logs/settings_clean_cache'
        const res = await fetch(api, ApiServices.requestOptions('POST', '', this.token))

        if (res.status === 200) {
          this.showSnackBar('הזיכרון הזמני נוקה בהצלחה', 'success')
        } else {
          this.showSnackBar('שגיאה בניקוי הזיכרון הזמני', 'error')
        }
      } catch (error) {
        this.showSnackBar('שגיאה: ' + error.message, 'error')
      }
    },

    showSnackBar(message, color) {
      this.snacbarText = message
      this.snackbarColorBt = color
      this.snackbar = true
    },

    setDateLogsToCurrentDate() {
      const today = new Date()
      this.dateLogs = today.toISOString().substr(0, 10)
    },
  },

  watch: {
    dateLogs() {
      // שינוי תאריך => מאפס paging + שומר את החיפוש הפעיל (appliedSearch)
      this.refreshDetails()
    },
    itemsPerPage() {
      // שינוי כמות שורות => חייבים להתחיל מחדש מהעמוד הראשון
      this.refreshDetails()
    },
    sortOrder() {
      // שינוי סדר מיון => חייבים להתחיל מחדש מהעמוד הראשון
      this.refreshDetails()
    },
  },

  created() {
    const isAdminSwitch = localStorage.getItem('tokenAdmin')
    if (Auth.getUserRole() === 'user' && isAdminSwitch) {
      this.token = localStorage.getItem('tokenAdmin')
    }

    this.setDateLogsToCurrentDate()
    this.getLogsPage(1)
  },
}
</script>

<style scoped>
.logs-component {
  min-height: 95vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f0f7ff 100%);
}

.logs-header {
  height: 64px;
  background: linear-gradient(to bottom, #dae1eb, #e2e8f0);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0d2c6d;
  box-shadow: 0 1px 3px rgba(13, 44, 109, 0.1);
}

.logs-card {
  min-height: 85vh;
  background: rgba(255, 255, 255, 0.98);
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(13, 44, 109, 0.08);
  backdrop-filter: blur(10px);
}

.filters-section {
  padding: 1.5rem;
  background: linear-gradient(to bottom, rgba(13, 44, 109, 0.02), transparent);
  border-bottom: 1px solid rgba(13, 44, 109, 0.1);
}

.filter-field {
  max-width: 220px;
}

.filter-field>>>.v-input__slot {
  border: 1px solid rgba(13, 44, 109, 0.1) !important;
  transition: all 0.3s ease;
}

.filter-field>>>.v-input__slot:hover {
  border-color: rgba(13, 44, 109, 0.2) !important;
  background: rgba(13, 44, 109, 0.03);
}

.refresh-button {
  color: #0d2c6d !important;
  background: rgba(13, 44, 109, 0.05);
  transition: all 0.3s ease;
}

.refresh-button:hover {
  background: rgba(13, 44, 109, 0.1) !important;
  transform: rotate(180deg);
}

.results-count {
  display: flex;
  align-items: center;
  color: #64748b;
}

.logs-list {
  padding: 1.5rem;
  padding-bottom: 4rem; /* ריפוד נוסף למטה כדי שהשורה האחרונה לא תיחתך */
  overflow-y: auto;
  height: calc(92vh - 240px);
}

/* מבטיח שהפריט האחרון יהיה גלוי במלואו */
.log-item:last-child {
  margin-bottom: 2rem;
}

.log-item {
  padding: 1rem;
  border-bottom: 1px solid rgba(13, 44, 109, 0.1);
  transition: all 0.3s ease;
}

.log-item:hover {
  background: rgba(13, 44, 109, 0.02);
}

.log-header {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  direction: ltr;
}

.log-index {
  font-weight: 600;
  color: #0d2c6d;
  margin-right: 1rem;
  min-width: 32px;
}

.log-timestamp {
  color: #64748b;
  margin-right: 1rem;
}

.log-level {
  font-size: 0.75rem;
  font-weight: 500;
}

.log-message {
  direction: ltr;
  color: #334155;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.log-message>>>strong {
  color: #0d2c6d;
  font-weight: 600;
}

/* Custom Scrollbar */
.logs-list::-webkit-scrollbar {
  width: 8px;
}

.logs-list::-webkit-scrollbar-track {
  background: rgba(13, 44, 109, 0.02);
  border-radius: 4px;
}

.logs-list::-webkit-scrollbar-thumb {
  background: rgba(13, 44, 109, 0.1);
  border-radius: 4px;
}

.logs-list::-webkit-scrollbar-thumb:hover {
  background: rgba(13, 44, 109, 0.2);
}

/* RTL Support */
.v-application {
  direction: rtl;
}

.v-text-field>>>.v-input__prepend-inner {
  margin-right: 0;
}

.pagination-section {
  padding: 1rem 1.5rem;
  border-top: 1px solid rgba(13, 44, 109, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.items-per-page {
  min-width: 200px;
}

.items-per-page-select {
  max-width: 200px;
}

.debug-info {
  border-top: 2px solid #ff9800;
}
</style>